import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DoodUserModel } from '@store/authentication/authentication.model';
import { DOOD_API } from '@config/ws.config';
import { HttpClient } from '@angular/common/http';
import { IPongoAsset, IPongoCustomer } from 'src/app/core/models/pongo.model';
import { DoodProductModel } from '@core/models/product.model';

export type PongoOrderingContext = 'kiosk' | 'click_and_collect' | null;

/**
 * Adds ordering_context only when it is known.
 * A null context is omitted so existing web payloads stay unchanged.
 */
export function withPongoOrderingContext<T extends object>(
  body: T,
  orderingContext?: PongoOrderingContext,
): T & { ordering_context?: Exclude<PongoOrderingContext, null> } {
  if (!orderingContext) {
    return body;
  }
  return { ...body, ordering_context: orderingContext };
}

@Injectable({
  providedIn: 'root',
})
export class PongoApiService {
  constructor(private readonly httpClient: HttpClient) {}

  getMarketplaceRewards$(marketplaceId: string): Observable<IPongoAsset[]> {
    return this.httpClient.get<IPongoAsset[]>(
      `${DOOD_API}/marketplaces/${marketplaceId}/pongo/rewards`,
    );
  }

  createCustomer$(
    marketplaceId: string,
    phoneNumber: string,
    hasOptin: Boolean,
    orderingContext?: PongoOrderingContext,
  ): Observable<DoodUserModel> {
    return this.httpClient.post<DoodUserModel>(
      `${DOOD_API}/marketplaces/${marketplaceId}/pongo/customer`,
      withPongoOrderingContext(
        {
          phone_number: phoneNumber,
          has_optin: hasOptin,
        },
        orderingContext,
      ),
    );
  }

  getCustomer$(marketplaceId: string, shopId?: string): Observable<IPongoCustomer> {
    let url = `${DOOD_API}/marketplaces/${marketplaceId}/pongo/customer`;
    if (shopId) {
      url += `?shop=${shopId}`;
    }

    return this.httpClient.get<IPongoCustomer>(url);
  }

  attachCustomer$(
    marketplaceId: string,
    phoneNumber: string,
    orderingContext?: PongoOrderingContext,
  ): Observable<DoodUserModel> {
    return this.httpClient.post<DoodUserModel>(
      `${DOOD_API}/marketplaces/${marketplaceId}/pongo/attach-customer`,
      withPongoOrderingContext(
        {
          phone_number: phoneNumber,
        },
        orderingContext,
      ),
    );
  }

  buyReward$(marketplaceId: string, rewardId: string, shopId?: string): Observable<IPongoAsset> {
    const body: { reward_id: string; shop_id?: string } = {
      reward_id: rewardId,
    };

    if (shopId) {
      body.shop_id = shopId;
    }

    return this.httpClient.post<IPongoAsset>(
      `${DOOD_API}/marketplaces/${marketplaceId}/pongo/buy-reward`,
      body,
    );
  }

  allowedProducts$(
    marketplaceId: string,
    externalIds: string[],
    shopId?: string,
  ): Observable<DoodProductModel[]> {
    const params = new URLSearchParams();
    externalIds.forEach(externalId => params.append('externalIds[]', externalId));
    if (shopId) {
      params.append('shopId', shopId);
    }

    const queryParamsString = params.toString();
    return this.httpClient.get<DoodProductModel[]>(
      `${DOOD_API}/marketplaces/${marketplaceId}/pongo/allowed-products?${queryParamsString}`,
    );
  }
}
